"use client";
import React, { useEffect, useRef, useState, useMemo } from "react";
import Hls from "hls.js";
import { VISIBLES } from "@/lib/idiomas";

// ✅ FIX: Constante fuera del componente para evitar loop infinito
// Un idioma oculto (lib/idiomas.js) no se prueba: sería un HEAD más que da 404 por video.
const DEFAULT_SUBTITLE_LANGS = ["es", "en", "pt", "it", "fr", "de", "ja", "tr"].filter((ln) => VISIBLES.includes(ln));

export const LANG_LABEL = {
  es: "Español",
  en: "English",
  pt: "Português",
  it: "Italiano",
  fr: "Français",
  de: "Deutsch",
  ja: "日本語",
  tr: "Türkçe",
};

/**
 * Props:
 * - type: 'hls' | 'youtube'
 * - src: string (m3u8 para HLS o URL de embed para YouTube)
 * - poster: string (solo HLS)
 * - autoPlay: boolean
 *
 * Subtítulos (NO YouTube):
 * - subtitleBasePath?: string
 *    Ej: "/subtitles/armonia-ilustrada-2/<slug>"
 *    De ahí busca: `${subtitleBasePath}/es.vtt`, `${subtitleBasePath}/en.vtt`, `${subtitleBasePath}/pt.vtt`
 * - subtitleLangs?: string[]  (default ["es","en","pt","it","fr","de","ja"])
 * - probeSubtitles?: boolean  (default true) En false no prueba los .vtt con HEAD y
 *    confía en subtitleLangs: lo usan las páginas del portal, que arman la lista mirando
 *    public/subtitles/ en el server (lib/portal/subtitulos.js).
 *
 * Alternativa manual:
 * - tracks?: Array<{ lang: string; label?: string; src: string; default?: boolean }>
 */
export default function VideoPlayer({
  type = "hls",
  src,
  poster,
  autoPlay = true,

  // Locale del sitio: si es "es" no se activa ningún subtítulo por defecto
  locale,

  // ✅ FIX: Usa la constante externa como default
  subtitleBasePath,
  subtitleLangs = DEFAULT_SUBTITLE_LANGS,
  probeSubtitles = true,

  // Modo manual (opcional)
  tracks,
}) {
  if (type === "youtube") {
    return (
      <>
        <div className="video-player video-player--youtube">
          <iframe
            src={src}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>

        <style jsx>{`
          .video-player {
            width: 100%;
            margin-inline: auto;
            border-radius: 12px;
            overflow: hidden;
          }
          .video-player--youtube {
            position: relative;
            padding-top: 56.25%;
          }
          .video-player--youtube iframe {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            border: 0;
          }
          @media (min-width: 768px) {
            .video-player {
              max-width: 900px;
            }
          }
        `}</style>
      </>
    );
  }

  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [availableTracks, setAvailableTracks] = useState([]);

  // Reemplaza todos los '#' por '%23' en cualquier URL
  const escapeHashes = (u) => (typeof u === "string" ? u.replace(/#/g, "%23") : u);

  // Reemplaza '+' literal por '%2B' en el path de la URL (S3/CloudFront lo necesita)
  const escapePlus = (u) => {
    if (typeof u !== "string") return u;
    const qIdx = u.indexOf("?");
    if (qIdx === -1) return u.replace(/\+/g, "%2B");
    return u.substring(0, qIdx).replace(/\+/g, "%2B") + u.substring(qIdx);
  };

  // Aplica todos los escapes necesarios para URLs de CloudFront/S3
  const escapeUrl = (u) => escapePlus(escapeHashes(u));

  // ✅ FIX: Función estable para verificar URLs (sin recrearse en cada render)
  const urlExists = useMemo(() => {
    return async (url, signal) => {
      try {
        // HEAD primero (rápido)
        const head = await fetch(url, { method: "HEAD", signal });
        if (head.ok) return true;

        // Algunas configs/dev servers no permiten HEAD -> probamos GET
        if (head.status === 405 || head.status === 403) {
          const get = await fetch(url, { method: "GET", signal });
          return get.ok;
        }
        return false;
      } catch {
        return false;
      }
    };
  }, []);

  // Construye tracks candidatos: o los pasás manualmente, o autodiscovery desde subtitleBasePath
  const candidateTracks = useMemo(() => {
    if (Array.isArray(tracks) && tracks.length > 0) {
      return tracks.map((t) => ({
        ...t,
        src: escapeHashes(t.src),
      }));
    }

    if (!subtitleBasePath) return [];

    const base = escapeHashes(subtitleBasePath.replace(/\/$/, "")); // sin trailing slash
    return subtitleLangs.map((lang) => ({
      lang,
      label: LANG_LABEL[lang] ?? lang,
      src: `${base}/${lang}.vtt`,
      // Si el locale es "es" no se activa ninguno; sino se activa el que coincide con el locale
      default: locale && locale !== "es" ? lang === locale : false,
    }));
  }, [tracks, subtitleBasePath, subtitleLangs, locale]);

  // Filtra tracks existentes para no mostrar opciones rotas
  useEffect(() => {
    const ac = new AbortController();

    async function run() {
      if (!candidateTracks.length) {
        setAvailableTracks([]);
        return;
      }

      const checks = probeSubtitles
        ? await Promise.all(
            candidateTracks.map(async (t) => ((await urlExists(t.src, ac.signal)) ? t : null))
          )
        : candidateTracks;

      const ok = checks.filter(Boolean);

      // Si ninguno quedó como default, marcamos el primero
      if (ok.length > 0 && !ok.some((t) => t.default)) {
        ok[0] = { ...ok[0], default: true };
      }

      if (!ac.signal.aborted) setAvailableTracks(ok);
    }

    run();
    return () => {
      ac.abort();
    };
  }, [candidateTracks, urlExists, probeSubtitles]);

  // HLS setup
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    video.crossOrigin = "anonymous";
    video.preload = "metadata";
    video.controls = true;

    // Si hls.js está soportado, lo usamos SIEMPRE para poder interceptar todas las URLs
    if (Hls.isSupported()) {
      // Loader que escapa '#' y '+' en cada request (manifest, playlists, segmentos)
      class SafeUrlLoader extends (Hls.DefaultConfig?.loader ?? Hls.loader) {
        load(context, config, callbacks) {
          if (context && typeof context.url === "string") {
            context.url = escapeUrl(context.url);
          }
          super.load(context, config, callbacks);
        }
      }

      const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: false,
        loader: SafeUrlLoader,
        maxBufferLength: 30,        // buffer máximo 30s adelante
        maxMaxBufferLength: 60,     // tope absoluto 60s (default 600s)
        maxBufferSize: 30_000_000,  // máx 30 MB en buffer
      });

      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(escapeUrl(src));
      });

      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        setReady(true);
        if (autoPlay) video.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        console.warn("HLS ERROR:", data.type, data.details);
      });

      return () => hls.destroy();
    }

    // Fallback (sin hls.js): al menos encodeo la URL raíz
    video.src = escapeUrl(src);
    video.onloadedmetadata = () => {
      setReady(true);
      if (autoPlay) video.play().catch(() => {});
    };
  }, [src, autoPlay]);

  return (
    <>
      <div className="video-player">
        <video
          ref={videoRef}
          className="video-player__media"
          poster={poster}
          playsInline
        >
          {/* Subtítulos */}
          {availableTracks.map((t) => (
            <track
              key={`${t.lang}-${t.src}`}
              kind="subtitles"
              srcLang={t.lang}
              label={t.label ?? t.lang}
              src={t.src}
              default={!!t.default}
            />
          ))}
        </video>
      </div>

      <style jsx>{`
        .video-player {
          width: 100%;
          margin-inline: auto;
          border-radius: 12px;
          overflow: hidden;
        }
        .video-player__media {
          display: block;
          width: auto;
          height: auto;
          max-width: 100%;
          border-radius: 12px;
        }
        @media (min-width: 768px) {
          .video-player {
            display: flex;
            max-width: 900px;
            align-items: center;
            justify-content: center;
            background: #111;
          }
          .video-player__media {
            max-height: min(58vh, 520px);
          }
        }
        @media (max-width: 767.98px) {
          .video-player__media {
            width: 100%;
            max-height: none;
          }
        }
      `}</style>
    </>
  );
}
